import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

import { QuoteRepository } from "../../quote.repository";
import { quoteLocationType } from "../quote-chips/quote-location-type.const";
import { Quote } from "../../quote.type";
import { QuoteLocation } from "../../models/quote-location.model";
import { NotifyService } from "../../../notify/notify.service";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { eventType } from "../../../notify/event.const";
import { BackendErrorPipe } from "../../../shared/backend/backend-error.pipe";
import { EventService } from "../../../shared/event/event.service";
import { LocationGroupRepository } from "app/location-group/location-group.repository";
import { LocationGroup } from "app/location-group/location-group.model";
import { PlaceDetails } from "app/shared/google-maps/place-autocomplete.service";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { AutoComplete } from "primeng/autocomplete";
import { FormsModule } from "@angular/forms";
import { Tooltip } from "primeng/tooltip";
import { LocationGroupSelectComponent } from "../../../ui/dropdown/location-group-select/location-group-select.component";
import { PlaceAutocompleteComponent } from "../../../ui/dropdown/place-autocomplete/place-autocomplete.component";

@Component({
  selector: "app-quote-chips-group",
  templateUrl: "./quote-chips-group.component.html",
  styleUrls: ["../quote-chips/quote-chips.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    AutoComplete,
    FormsModule,
    Tooltip,
    LocationGroupSelectComponent,
    PlaceAutocompleteComponent,
  ],
})
export class QuoteChipsGroupComponent implements OnInit {
  private _eref = inject(ElementRef);
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private locationGroupRepository = inject(LocationGroupRepository);

  public types = quoteLocationType;

  public readonly type = input<string>(undefined);
  public readonly locationKindFilter = input("");
  public readonly locationKindFilterOptions = input([]);
  public readonly quote = input<Quote>(undefined);

  readonly locationChange = output<{
    type: string;
    locations: QuoteLocation[];
  }>();
  readonly locationKindFilterChange = output<string>();

  public readonly locationGroupSelect = viewChild(LocationGroupSelectComponent);
  public addingLocation = false;

  // Set while the picked place's details are in flight, so the leg cannot be
  // saved before its coordinates and address have landed.
  public placePending = false;
  public hoveredLocationGroup: LocationGroup;

  // TODO: This should be converted to Reactive Form, or QuoteLocation model
  private _locations = [];
  public currentEditedLocation: QuoteLocation = new QuoteLocation();
  private expanded = true;

  ngOnInit() {
    this.expanded = this.locations.length ? false : true;
  }

  @HostListener("document: mousedown", ["$event"]) onMouseDown(event) {
    if (
      !this._eref.nativeElement.contains(event.target) &&
      !this.hasSomeParentTheClass(event.target, "p-select-overlay") &&
      !this.hasSomeParentTheClass(event.target, "ui-dropdown-item") &&
      !this.hasSomeParentTheClass(event.target, "ui-dropdown-filter") &&
      !this.hasSomeParentTheClass(event.target, "ui-autocomplete-list-item") &&
      !!this.locations &&
      this.locations.length
    ) {
      this.expanded = false;
      this.looseFocusOnEditedLocation();
    } else if (
      !this.expanded &&
      this._eref.nativeElement.contains(event.target)
    ) {
      this.expanded = true;
      if (!this.hasSomeParentTheClass(event.target, "p-chips-token-icon")) {
        if (this.locations && this.locations.length) {
          this.currentEditedLocation = this.locations[0];
        }
      }
    }
  }

  public onPlaceSelect(place: PlaceDetails): void {
    this.currentEditedLocation.inlandHaulage = place.label;
    this.currentEditedLocation.latitude = place.latitude;
    this.currentEditedLocation.longitude = place.longitude;
    this.currentEditedLocation.postalCode = place.postalCode ?? "00-000";
    this.currentEditedLocation.country = place.country ?? "";

    this.refreshLocationGroup(this.currentEditedLocation.country);
  }

  @Input()
  set locations(locations: QuoteLocation[]) {
    if (locations !== null) {
      this._locations = locations;
    }
  }

  get locations(): QuoteLocation[] {
    return this._locations;
  }

  public isExpanded(): boolean {
    return this.expanded || !this.locations.length;
  }

  public editLocation(location: QuoteLocation): void {
    this.currentEditedLocation = location;
  }

  public looseFocusOnEditedLocation() {
    this.currentEditedLocation = new QuoteLocation();
    this.expanded = false;
  }

  public isValid() {
    return this.currentEditedLocation.getLocationGroup();
  }

  public removeElement(event) {
    const endpoint = this.isOrigin() ? "deleteOrigin" : "deleteDestination";

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      event.value.getId(),
    ).subscribe({
      next: (response): void => {
        this.looseFocusOnEditedLocation();
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.SUCCESS, "Location has been removed");
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Removing location error");
      },
    });
  }

  // Only an emptied field is written back: the picked place, not the typed
  // text, is what fills the leg here.
  public onInlandHaulageChange(inlandHaulage: string): void {
    this.clearDistance();

    if (!inlandHaulage) {
      this.currentEditedLocation.inlandHaulage = inlandHaulage;
      this.currentEditedLocation.postalCode = null;
      this.currentEditedLocation.country = null;
    }
  }

  public clearDistance() {
    this.currentEditedLocation.latitude = null;
    this.currentEditedLocation.longitude = null;
  }

  private refreshLocationGroup(country: string) {
    if (country && !this.currentEditedLocation.locationGroup) {
      this.locationGroupRepository.getByCountry(country).subscribe({
        next: (locationGroup): void => {
          this.currentEditedLocation.locationGroup = locationGroup;
        },
        error: (err): void => {},
      });
    }
  }

  private addElement() {
    const endpoint = this.isOrigin() ? "addOrigin" : "addDestination";

    this.addingLocation = true;
    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      undefined,
      this.currentEditedLocation.getInlandHaulage(),
      this.currentEditedLocation.getPostalCode(),
      this.currentEditedLocation.getLatitude(),
      this.currentEditedLocation.getLongitude(),
      this.currentEditedLocation.getCountry(),
      this.currentEditedLocation.getLocationGroup()?.getId(),
    ).subscribe({
      next: (quoteLocation): void => {
        this.addingLocation = false;
        this.locations.push(quoteLocation);
        this.locations = [...this.locations];
        this.looseFocusOnEditedLocation();
        this.locationGroupSelect()?.resetTouchedState();
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
        this.notifyService.toast(TOAST.SUCCESS, "Location has been added");
        this.emitQuoteUpdated();
      },
      error: (err): void => {
        this.addingLocation = false;
        this.notifyService.toast(
          TOAST.ERROR,
          "Adding location error",
          new BackendErrorPipe().transform(err, "string"),
        );
      },
    });
  }

  private saveInland() {
    const endpoint = this.isOrigin() ? "patchOrigin" : "patchDestination";

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      this.currentEditedLocation.getId(),
      undefined,
      this.currentEditedLocation.getInlandHaulage(),
      this.currentEditedLocation.getPostalCode(),
      this.currentEditedLocation.getLatitude(),
      this.currentEditedLocation.getLongitude(),
      this.currentEditedLocation.getCountry(),
      this.currentEditedLocation.getLocationGroup()?.getId(),
    ).subscribe({
      next: (quoteLocation): void => {
        this.emitQuoteUpdated();
        this.looseFocusOnEditedLocation();
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
        this.notifyService.toast(TOAST.SUCCESS, "Location has been added");
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Adding location error");
      },
    });
  }

  private hasSomeParentTheClass(element, classname) {
    if (
      typeof element.className === "string" &&
      element.className.split(" ").indexOf(classname) >= 0
    ) {
      return true;
    }

    if (
      element.className instanceof SVGAnimatedString &&
      element.className.baseVal.split(" ").indexOf(classname) >= 0
    ) {
      return true;
    }

    return (
      element.parentNode &&
      this.hasSomeParentTheClass(element.parentNode, classname)
    );
  }

  private emitQuoteUpdating(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote(),
    });
  }

  private emitQuoteUpdated(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote(),
    });
  }

  private isOrigin(): boolean {
    return this.type() === quoteLocationType.origin;
  }
}
